import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell, AlarmClock, Share2, Inbox as InboxIcon, LifeBuoy, MapPin,
  HandHeart, Building2, ArrowUpRight, Clock3, EyeOff, BellOff, Filter, RefreshCw,
  ClipboardCheck,
} from "lucide-react";

/** Type → icon + label + color used to render each row's badge. */
const TYPE_META = {
  overdue_task: { icon: AlarmClock, label: "Overdue task", color: "bg-[#FEE2E2] text-[#991B1B]" },
  stale_referral: { icon: Share2, label: "Stale referral", color: "bg-[#FEF3C7] text-[#92400E]" },
  intake_request: { icon: InboxIcon, label: "New /refer", color: "bg-[#E0E7FF] text-[#1E3A8A]" },
  support_ticket: { icon: LifeBuoy, label: "Support ticket", color: "bg-[#FDE68A] text-[#92400E]" },
  overdue_checkin: { icon: MapPin, label: "Overdue check-in", color: "bg-[#FEE2E2] text-[#991B1B]" },
  pending_volunteer: { icon: HandHeart, label: "Volunteer applicant", color: "bg-[#E8ECE7] text-[#166534]" },
  pending_partner_submission: { icon: Building2, label: "Partner submission", color: "bg-[#E0E7FF] text-[#1E3A8A]" },
  new_assessment_submission: { icon: ClipboardCheck, label: "New assessment", color: "bg-[#DCFCE7] text-[#166534]" },
};

const ORDER = [
  "overdue_checkin", "overdue_task", "intake_request", "stale_referral",
  "support_ticket", "pending_volunteer", "pending_partner_submission",
  "new_assessment_submission",
];

export default function SmartInbox() {
  const [data, setData] = useState({ items: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // empty = all
  const [pending, setPending] = useState({}); // key → busy

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox");
      setData(data || { items: [], total: 0, counts: {} });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const snooze = async (it) => {
    setPending((p) => ({ ...p, [it.key]: true }));
    try {
      await api.post("/inbox/snooze", { item_id: it.id, type: it.type });
      toast.success("Snoozed for 24 hours");
      setData((d) => ({
        ...d,
        items: d.items.filter((x) => x.key !== it.key),
        total: Math.max(0, d.total - 1),
      }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPending((p) => ({ ...p, [it.key]: false })); }
  };

  const dismiss = async (it) => {
    if (!window.confirm("Hide this item from your inbox permanently? (it doesn't close the underlying task)")) return;
    setPending((p) => ({ ...p, [it.key]: true }));
    try {
      await api.post("/inbox/dismiss", { item_id: it.id, type: it.type });
      toast.success("Dismissed");
      setData((d) => ({
        ...d,
        items: d.items.filter((x) => x.key !== it.key),
        total: Math.max(0, d.total - 1),
      }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPending((p) => ({ ...p, [it.key]: false })); }
  };

  const items = filter ? data.items.filter((i) => i.type === filter) : data.items;

  return (
    <div className="space-y-6" data-testid="smart-inbox-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="hw-eyebrow mb-2">Operations</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A] flex items-center gap-2">
            <Bell className="w-7 h-7 text-[#1E3A8A]" strokeWidth={1.75} />
            Smart Inbox
          </h1>
          <p className="text-[#334155] mt-2 max-w-2xl">
            Everything that needs your attention — across tasks, referrals, partner submissions,
            check-ins, and more — in one place. Snooze for a day or dismiss to keep the queue tidy.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="inbox-refresh"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-sm text-[#334155] hover:bg-[#F9F8F6]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="inbox-counts">
        {ORDER.map((t) => {
          const m = TYPE_META[t];
          const count = data.counts?.[t] || 0;
          const active = filter === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setFilter(active ? "" : t)}
              data-testid={`inbox-filter-${t}`}
              className={`text-left rounded-2xl border p-3 transition-colors ${
                active
                  ? "border-[#1E3A8A] bg-[#E0E7FF]"
                  : "border-[#E5E5E0] bg-white hover:border-[#1E3A8A]"
              }`}
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#1E3A8A]">
                <m.icon className="w-3 h-3" />
                <span className="truncate">{m.label}</span>
              </div>
              <div className={`text-2xl font-display font-semibold mt-1 ${count > 0 ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
                {count}
              </div>
            </button>
          );
        })}
      </div>

      {filter && (
        <div className="flex items-center gap-2 text-sm text-[#334155]" data-testid="inbox-active-filter">
          <Filter className="w-3.5 h-3.5" />
          Filtering by <strong>{TYPE_META[filter]?.label}</strong>
          <button
            type="button"
            onClick={() => setFilter("")}
            data-testid="inbox-clear-filter"
            className="text-[#1E3A8A] hover:underline ml-1"
          >
            Clear
          </button>
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-[#E5E5E0] rounded-2xl h-20 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="inbox-empty">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#E8ECE7] text-[#166534] flex items-center justify-center mb-3">
            <Bell className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Inbox zero 🎉</h2>
          <p className="text-sm text-[#334155] mt-2 max-w-md mx-auto">
            Nothing needs your attention right now. New items will appear here as they age in.
          </p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="inbox-list">
          {items.map((it) => (
            <InboxRow
              key={it.key}
              item={it}
              busy={!!pending[it.key]}
              onSnooze={() => snooze(it)}
              onDismiss={() => dismiss(it)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function InboxRow({ item, busy, onSnooze, onDismiss }) {
  const meta = TYPE_META[item.type] || { icon: Bell, label: item.type, color: "bg-[#F1F5F9] text-[#475569]" };
  return (
    <li
      data-testid={`inbox-row-${item.type}-${item.id}`}
      className="bg-white border border-[#E5E5E0] rounded-2xl p-4 hw-card-hover"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.color}`}>
          <meta.icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.color}`}>
              {meta.label}
            </span>
            {item.external_source === "unite_us" && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#DBEAFE] text-[#1E40AF]">
                Unite Us
              </span>
            )}
            <span className="text-xs text-[#1E3A8A] inline-flex items-center gap-1">
              <Clock3 className="w-3 h-3" /> {formatAge(item.age_minutes)}
            </span>
          </div>
          <div className="font-medium text-[#0F172A]" data-testid={`inbox-row-title-${item.id}`}>
            {item.title}
          </div>
          <div className="text-xs text-[#334155] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {item.assignee_name && <span>Assigned to {item.assignee_name}</span>}
            {item.referrer && <span>Referrer: {item.referrer}</span>}
            {item.counties?.length > 0 && <span>{item.counties.join(", ")}</span>}
            {item.urgency && <span className="uppercase">{item.urgency}</span>}
            {item.primary_need && <span>{item.primary_need}</span>}
            {item.severity && <span className="uppercase">{item.severity}</span>}
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {item.deep_link && (
            <Link
              to={item.deep_link}
              data-testid={`inbox-open-${item.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554]"
            >
              Open <ArrowUpRight className="w-3 h-3" />
            </Link>
          )}
          <button
            type="button"
            onClick={onSnooze}
            disabled={busy}
            title="Snooze 24 hours"
            data-testid={`inbox-snooze-${item.id}`}
            className="p-2 rounded-full text-[#334155] hover:bg-[#F9F8F6] disabled:opacity-50"
          >
            <BellOff className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            title="Dismiss permanently"
            data-testid={`inbox-dismiss-${item.id}`}
            className="p-2 rounded-full text-[#334155] hover:bg-[#F9F8F6] disabled:opacity-50"
          >
            <EyeOff className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
}

function formatAge(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
